/**
 * Shop All diet & allergen filters
 * (docs/superpowers/specs/2026-09-30-dietary-allergen-filters-design.md).
 *
 * Hides non-matching cards with its OWN class, dietary-hidden -- never touching
 * availability-hidden -- then hands the recount/collapse/subnav refresh to
 * StockedAvailability.refreshScopes(), which treats a card as hidden if EITHER
 * filter hid it. Matching rules and URL parsing live in the pure, unit-tested
 * assets/dietary-filter-match.js.
 *
 * Load order: availability-filter.js is deferred from <head>, this file from the
 * section, so availability's DOMContentLoaded handler runs first. Both handlers
 * run in the same task, so a URL-filtered card never paints before it's hidden.
 */
(function () {
  var HIDDEN = 'dietary-hidden';
  var match = window.StockedDietaryFilterMatch;

  var scope, form, toggles, activeRow, pills, emptyEl;
  var state = null;

  function availability() {
    return window.StockedAvailability || null;
  }

  // Same grid-slot wrapper availability-filter.js hides, so both classes land on
  // one element and refreshScopes() sees them together.
  function wrapperOf(card) {
    var api = availability();
    var selector = api && api.CARD_WRAPPER_SELECTOR;
    return (selector && card.closest(selector)) || card;
  }

  function isHidden(card) {
    var api = availability();
    if (api && typeof api.isCardHidden === 'function') return api.isCardHidden(card);
    return wrapperOf(card).classList.contains(HIDDEN);
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function cards() {
    return scope.querySelectorAll('[data-product-card]');
  }

  function checkboxes() {
    return form.querySelectorAll('input[type="checkbox"]');
  }

  // The options actually on the page, per param -- parseState() only accepts these.
  function knownHandles() {
    var known = match.emptyState();
    each(checkboxes(), function (input) {
      if (known[input.name]) known[input.name].push(input.value);
    });
    return known;
  }

  function readForm() {
    var next = match.emptyState();
    each(checkboxes(), function (input) {
      if (input.checked && next[input.name]) next[input.name].push(input.value);
    });
    return next;
  }

  function writeForm(next) {
    each(checkboxes(), function (input) {
      input.checked = !!next[input.name] && next[input.name].indexOf(input.value) !== -1;
    });
  }

  function countKeys(toggle) {
    return (toggle.getAttribute('data-dietary-count-keys') || '').split(',').filter(Boolean);
  }

  // The dropdown toggle that owns a param, so focus can land somewhere sensible
  // after the element the customer clicked removes or hides itself.
  function toggleFor(name) {
    var found = null;
    each(toggles, function (toggle) {
      if (!found && countKeys(toggle).indexOf(name) !== -1) found = toggle;
    });
    return found || toggles[0];
  }

  // Pill text: the option's own label, except allergens, whose options are bare
  // nouns ("Nuts") under a "Hide dishes containing" legend -- out of that
  // context the pill must say what it does ("No nuts").
  function pillLabel(name, value) {
    var label = value;
    each(checkboxes(), function (input) {
      if (input.name === name && input.value === value) {
        var text = input.closest('label') && input.closest('label').querySelector('.dietary-filter__option-label');
        if (text) label = text.textContent.trim();
      }
    });
    return name === 'allergen' ? 'No ' + label.toLowerCase() : label;
  }

  function renderPills() {
    pills.textContent = '';
    match.KEYS.forEach(function (name) {
      state[name].forEach(function (value) {
        var label = pillLabel(name, value);
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dietary-filter__pill';
        btn.setAttribute('data-dietary-remove', name);
        btn.value = value;
        btn.setAttribute('aria-label', 'Remove filter: ' + label);
        btn.textContent = label;
        li.appendChild(btn);
        pills.appendChild(li);
      });
    });
  }

  // Each toggle counts the params listed in its data-dietary-count-keys -- the
  // Filter button lists all three, so "Filter (3)" counts every active option.
  function renderToggles() {
    each(toggles, function (toggle) {
      var count = countKeys(toggle).reduce(function (sum, key) {
        return sum + (state[key] ? state[key].length : 0);
      }, 0);
      var base = toggle.getAttribute('data-dietary-label');
      var label = toggle.querySelector('[data-dietary-toggle-label]');
      if (label) label.textContent = count ? base + ' (' + count + ')' : base;
    });
  }

  function visibleCount() {
    return Array.prototype.reduce.call(
      cards(),
      function (n, card) { return isHidden(card) ? n : n + 1; },
      0
    );
  }

  // Everything derived from the current DOM state. Also rerun after every
  // availability pass, so a week change updates the empty state without the
  // customer touching an option.
  function renderChrome() {
    var active = match.activeCount(state);
    renderToggles();
    renderPills();
    activeRow.hidden = active === 0;
    if (emptyEl) emptyEl.hidden = !(active > 0 && visibleCount() === 0);
  }

  function filterCards() {
    each(cards(), function (card) {
      var visible = match.isVisible(
        {
          diet: match.splitHandles(card.getAttribute('data-diet')),
          allergens: match.splitHandles(card.getAttribute('data-allergens')),
        },
        state
      );
      wrapperOf(card).classList.toggle(HIDDEN, !visible);
    });
  }

  function refreshScopes() {
    var api = availability();
    if (api && typeof api.refreshScopes === 'function') api.refreshScopes();
  }

  function apply() {
    try {
      filterCards();
      refreshScopes();
      renderChrome();
    } catch (err) {
      // Fail OPEN, like availability-filter.js: an unfiltered menu beats a blank
      // one. Only this filter's class is cleared, so week filtering is untouched.
      console.error('[StockedDietaryFilter] apply() failed; diet filters left off', err);
      each(scope.querySelectorAll('.' + HIDDEN), function (el) {
        el.classList.remove(HIDDEN);
      });
      refreshScopes();
    }
  }

  function setState(next) {
    state = next;
    writeForm(state);
    var search = match.writeState(window.location.search, state);
    history.replaceState(history.state, '', window.location.pathname + search + window.location.hash);
    apply();
  }

  // Dropdowns: plain disclosures (not modal), one open at a time. Ticking an
  // option filters immediately and keeps its menu open.
  function menuFor(toggle) {
    return document.getElementById(toggle.getAttribute('aria-controls'));
  }

  function setOpen(toggle, open) {
    var menu = menuFor(toggle);
    if (!menu) return;
    menu.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function openToggle() {
    var found = null;
    each(toggles, function (toggle) {
      if (toggle.getAttribute('aria-expanded') === 'true') found = toggle;
    });
    return found;
  }

  function closeAll(except) {
    each(toggles, function (toggle) {
      if (toggle !== except) setOpen(toggle, false);
    });
  }

  function bind() {
    each(toggles, function (toggle) {
      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') !== 'true';
        closeAll(toggle);
        setOpen(toggle, open);
      });
    });

    form.addEventListener('change', function () { setState(readForm()); });
    form.addEventListener('submit', function (event) { event.preventDefault(); });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var toggle = openToggle();
      if (!toggle) return;
      setOpen(toggle, false);
      toggle.focus();
    });

    document.addEventListener('click', function (event) {
      // Any click outside the filter row closes an open menu.
      if (!form.contains(event.target)) closeAll(null);

      // Clear buttons live in the filter row and the empty state, and pills are
      // re-rendered on every change, so delegate. Each of these hides or removes
      // the element that was clicked; move focus rather than drop it to <body>.
      if (event.target.closest('[data-dietary-clear]')) {
        setState(match.emptyState());
        if (toggles[0]) toggles[0].focus();
        return;
      }
      var pill = event.target.closest('[data-dietary-remove]');
      if (pill) {
        var name = pill.getAttribute('data-dietary-remove');
        var next = readForm();
        next[name] = next[name].filter(function (h) { return h !== pill.value; });
        setState(next);
        var owner = toggleFor(name);
        if (owner) owner.focus();
      }
    });

    document.addEventListener('stocked:availability-applied', function () {
      if (state) renderChrome();
    });
  }

  function init() {
    scope = document.querySelector('[data-dietary-scope]');
    form = document.querySelector('[data-dietary-form]');
    if (!scope || !form || !match) return;

    toggles = form.querySelectorAll('[data-dietary-toggle]');
    activeRow = form.querySelector('[data-dietary-active]');
    pills = activeRow && activeRow.querySelector('[data-dietary-pills]');
    emptyEl = document.querySelector('[data-dietary-empty]');
    if (!toggles.length || !activeRow || !pills) return;

    state = match.parseState(window.location.search, knownHandles());
    writeForm(state);
    bind();
    apply();
  }

  document.addEventListener('DOMContentLoaded', init);
})();
